import React from 'react';
import { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Grid } from '@statsbomb/kitbag-components';
import { isEmpty } from 'lodash';
import PlayerPassEndsTile from './PlayerPassEnds.Tile';
import PlayerPassPlacementTile from './PlayerPassPlacement.Tile';
import PlayerSnapTile from './PlayerSnap.Tile';
import PlayerRadarTile from './PlayerRadar.Tile';
import PlayerStatsTile from './PlayerStats.Tile';
import PlayerTacklingTile from './PlayerTackling.Tile';
import PlayerHavocTile from './PlayerHavoc.Tile';
import { mf_PlayerDetails } from '../../../apollo';
import { useReactiveState } from '../../../utils/hooks/useReactiveState';
import {
  PlayerHavocTileContainer,
  PlayerPassEndsTileContainer,
  PlayerSnapTileContainer,
  PlayerTacklingTileContainer,
  TileContentVerticallyCentered,
} from './PlayerOverview.styles';
import {
  PASS_PLACEMENT_COLOR_MODES,
  PASS_PLACEMENT_SCALES,
} from '../../../visualisations/PassPlacement/PassPlacement.constants';
import { GRAPH_OPTIONS } from '../../../visualisations/PassingChart/PassingChart.constants';
import PlayerRoutesTile from './PlayerRoutes.Tile';
import PlayerComparisonTile from './PlayerComparison.Tile';
import { KitbagPageGridHolder } from '../../../components/Layout/Layout.styles';
import PageHeader from '../../../components/PageHeader/PageHeader';
import PlayerBioTile from './PlayerBio.Tile';
import {
  playerDetailsPropTypes,
  teamDetailsPropTypes,
} from '../../../apollo/index.data.propTypes';

const PlayerOverview = ({
  __storybookPlayerDetails,
  __storybookTeamDetails,
  __storybookCompetition,
}) => {
  const theme = useTheme();
  const playerDetails =
    __storybookPlayerDetails || useReactiveState(mf_PlayerDetails);
  const { mostCommonPosition: position, id: playerId } = playerDetails[0] || {};
  // show radar and stats for these positions only
  const showRadars = ['QB', 'LB', 'DB', 'DL', 'WR', 'TE', 'OL', 'RB'].includes(
    position?.code
  );

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <Grid item xs={12}>
          <PageHeader
            __storybookPlayerDetails={
              !isEmpty(__storybookPlayerDetails)
                ? __storybookPlayerDetails[0]
                : null
            }
            __storybookTeamDetails={__storybookTeamDetails}
          />
        </Grid>
        <Grid item xs={12}>
          <PlayerBioTile __storybookCompetition={__storybookCompetition} />
        </Grid>
        <Grid container={false}>
          <div
            style={{
              margin: `${theme.sizing.small} 0 0 0`,
              display: 'flex',
              width: 'calc(100% - 0.5rem)',
              height: showRadars ? '516px' : 'auto',
            }}
          >
            {showRadars && (
              <Grid item xs={6}>
                <PlayerRadarTile
                  __storybookPlayerDetails={
                    !isEmpty(__storybookPlayerDetails)
                      ? __storybookPlayerDetails[0]
                      : null
                  }
                  __storybookTeamDetails={__storybookTeamDetails}
                />
              </Grid>
            )}
            <Grid item xs={showRadars ? 6 : 12}>
              <PlayerStatsTile />
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12}>
          {playerDetails && position?.code === 'QB' && (
            <>
              <Grid>
                <Grid item xs={3}>
                  {playerId && (
                    <PlayerComparisonTile
                      margin="0 0 0 0"
                      playerId={playerId}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <PlayerPassEndsTile
                    graphOptionDefault={GRAPH_OPTIONS[2].value}
                  />
                </Grid>
                <Grid item xs={5}>
                  <TileContentVerticallyCentered>
                    <PlayerPassPlacementTile
                      selectedScale={PASS_PLACEMENT_SCALES[0]} // 2:1 aspect ratio
                    />
                  </TileContentVerticallyCentered>
                </Grid>
              </Grid>
            </>
          )}

          {playerDetails && position?.code === 'WR' && (
            <Grid>
              <Grid item xs={6}>
                <PlayerPassEndsTile />
              </Grid>

              <Grid item xs={2}>
                <PlayerSnapTile padding="0 11%" />
              </Grid>

              <Grid item xs={4}>
                <PlayerRoutesTile />
              </Grid>

              <Grid item xs={5}>
                <PlayerPassPlacementTile
                  selectedScale={PASS_PLACEMENT_SCALES[1]} // 3:2 aspect ratio
                />
              </Grid>

              <Grid item xs={3}>
                {playerId && (
                  <PlayerComparisonTile margin="0 0 0 0" playerId={playerId} />
                )}
              </Grid>
            </Grid>
          )}

          {playerDetails && position?.code === 'RB' && (
            <>
              <Grid>
                <Grid item xs={5}>
                  <PlayerTacklingTileContainer $marginBottom="-20px">
                    <PlayerTacklingTile />
                  </PlayerTacklingTileContainer>
                </Grid>

                <Grid item xs={2}>
                  <PlayerSnapTileContainer>
                    <PlayerSnapTile padding="0 17%" />
                  </PlayerSnapTileContainer>
                </Grid>

                <Grid item xs={5}>
                  <PlayerPassEndsTileContainer>
                    <PlayerPassEndsTile />
                  </PlayerPassEndsTileContainer>
                </Grid>

                <Grid item xs={3}>
                  {playerId && (
                    <PlayerComparisonTile
                      margin="0 0 0 0"
                      playerId={playerId}
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}

          {playerDetails && position?.code === 'TE' && (
            <Grid>
              <Grid item xs={4}>
                <PlayerPassEndsTile />
              </Grid>

              <Grid item xs={2}>
                <PlayerSnapTileContainer>
                  <PlayerSnapTile padding="0 11%" />
                </PlayerSnapTileContainer>
              </Grid>

              <Grid item xs={4}>
                <PlayerRoutesTile />
              </Grid>

              <Grid item xs={5}>
                <PlayerPassPlacementTile
                  selectedScale={PASS_PLACEMENT_SCALES[1]} // 3:2 aspect ratio
                  colorMode={PASS_PLACEMENT_COLOR_MODES.SUCCESS}
                />
              </Grid>

              <Grid item xs={3}>
                {playerId && (
                  <PlayerComparisonTile margin="0 0 0 0" playerId={playerId} />
                )}
              </Grid>
            </Grid>
          )}

          {playerDetails &&
            (position?.code === 'DL' ||
              position?.code === 'LB' ||
              position?.code === 'DB') && (
              <Grid>
                <Grid item xs={5}>
                  <PlayerTacklingTileContainer
                    $marginBottom="-20px"
                    $maxWidth="650px"
                  >
                    <PlayerTacklingTile />
                  </PlayerTacklingTileContainer>
                </Grid>

                <Grid item xs={7}>
                  <PlayerHavocTileContainer $marginBottom="-20px">
                    <PlayerHavocTile />
                  </PlayerHavocTileContainer>
                  <PlayerComparisonTile
                    margin="0.5rem 0 0 0"
                    width="50%"
                    playerId={playerId}
                  />
                </Grid>
              </Grid>
            )}

          {playerDetails && position?.code === 'OL' && (
            <Grid>
              <Grid item xs={4}>
                {playerId && (
                  <PlayerComparisonTile margin="0 0 0 0" playerId={playerId} />
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </KitbagPageGridHolder>
  );
};

PlayerOverview.propTypes = {
  __storybookPlayerDetails: playerDetailsPropTypes,
  __storybookTeamDetails: teamDetailsPropTypes,
  __storybookCompetition: PropTypes.number,
};

export default PlayerOverview;
